import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { FileLock2, FileText, Loader2, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { useTRPC } from "@/trpc";

const ORG_NOTE_MAX_LENGTH = 5000;

interface TimeEntryOrgNoteProps {
  timeEntryId: string;
  organizationBusinessUnitId: string;
  orgNote: string | null;
  canEdit: boolean;
  popover?: boolean;
  onSaved: () => void;
}

export function TimeEntryOrgNote({
  timeEntryId,
  organizationBusinessUnitId,
  orgNote,
  canEdit,
  popover = false,
  onSaved,
}: TimeEntryOrgNoteProps) {
  const trpc = useTRPC();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(orgNote ?? "");

  const updateMutation = useMutation(
    trpc.timeEntry.updateOrgNote.mutationOptions({
      onSuccess: () => {
        toast.success("Org note saved");
        setOpen(false);
        onSaved();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to save org note");
      },
    }),
  );

  const saveNote = () => {
    const trimmedNote = note.trim();
    setNote(trimmedNote);
    updateMutation.mutate({
      organizationBusinessUnitId,
      timeEntryId,
      orgNote: trimmedNote || null,
    });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setNote(orgNote ?? "");
    }
    setOpen(nextOpen);
  };

  if (!canEdit && !orgNote) {
    return null;
  }

  if (popover) {
    const hasChanges = note.trim() !== (orgNote ?? "");

    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={
              orgNote
                ? "h-8 gap-1.5 whitespace-nowrap border-blue-200 bg-blue-50 px-2 text-xs font-medium text-blue-700 hover:bg-blue-100 hover:text-blue-800"
                : "h-8 gap-1.5 whitespace-nowrap px-2 text-xs text-slate-600"
            }
          >
            {orgNote ? (
              <FileText className="h-3.5 w-3.5" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            {orgNote ? "View org note" : "Add org note"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 space-y-3" align="end">
          <div>
            <p className="text-sm font-medium">Org Note</p>
            <p className="text-xs text-slate-500">Hidden from the provider</p>
          </div>
          {canEdit ? (
            <>
              <Textarea
                aria-label="Org Note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                maxLength={ORG_NOTE_MAX_LENGTH}
                rows={4}
                placeholder="Add context for your business unit..."
                disabled={updateMutation.isPending}
                className="resize-y text-sm"
              />
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-slate-500">
                  {note.length.toLocaleString()} /{" "}
                  {ORG_NOTE_MAX_LENGTH.toLocaleString()}
                </span>
                <Button
                  type="button"
                  size="sm"
                  onClick={saveNote}
                  disabled={!hasChanges || updateMutation.isPending}
                >
                  {updateMutation.isPending && (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  )}
                  Save note
                </Button>
              </div>
            </>
          ) : (
            <p className="whitespace-pre-wrap break-words text-sm text-slate-700">
              {orgNote}
            </p>
          )}
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      <div className="min-w-36 max-w-64 space-y-1.5">
        {orgNote ? (
          <p className="whitespace-pre-wrap break-words text-sm text-slate-700">
            {orgNote}
          </p>
        ) : (
          <p className="text-xs italic text-slate-400">No internal note</p>
        )}
        {canEdit && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-slate-600"
            onClick={() => handleOpenChange(true)}
          >
            {orgNote ? (
              <Pencil className="mr-1 h-3.5 w-3.5" />
            ) : (
              <Plus className="mr-1 h-3.5 w-3.5" />
            )}
            {orgNote ? "Edit note" : "Add note"}
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileLock2 className="h-5 w-5 text-slate-600" />
              Org Note
            </DialogTitle>
            <DialogDescription>
              Only authorized users in this business unit can see this note. It
              is never shown to the provider.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Label htmlFor={`organization-note-${timeEntryId}`}>Note</Label>
            <Textarea
              id={`organization-note-${timeEntryId}`}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              maxLength={ORG_NOTE_MAX_LENGTH}
              rows={6}
              placeholder="Add context for your business unit..."
              disabled={updateMutation.isPending}
            />
            <p className="text-right text-xs text-slate-500">
              {note.length.toLocaleString()} /{" "}
              {ORG_NOTE_MAX_LENGTH.toLocaleString()}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveNote}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
